
import { LogicalExpression } from "@liminil/data-filter";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { CSSProperties, createRef, useCallback, useEffect, useState } from "react";
import { Field, RuleGroupType, defaultOperators } from "react-querybuilder";
import { FabrixIcon } from "../../common/icons";
import { useIcon, useTranslation, useUxManagement } from "../../hooks";
import useElementLoaded from "../../hooks/use-element-loaded";
import FilterEditor, { FilterEditorRef, initQuery } from "../query-editor";
import { expressionToQuery, queryToExpression } from "./filter-editor-utils";


export interface ExpressionField {
    id: string;
    name: string;
    dataType: string;
    label?: string;
}


export interface DialogPosition {
    x: number, y: number
}

export interface ExpressionDialogProps {
    open: boolean;
    onSave: (filter?: LogicalExpression, close?: boolean) => void;
    onCancel: () => void;
    filter?: LogicalExpression;
    scope?: string;
    fields: ExpressionField[];
    title?: string;
}


interface FilterUxData {
    position?: DialogPosition;
    width?: number;
    height?: number;
}

/**
 * Provides an interactive dialog for building logical filter expressions with react-querybuilder.
 * @param props Dialog configuration including initial filter, fields, and callbacks.
 * @returns PrimeReact dialog containing the filter editor.
 */
export default function ExpressionDialog(props: ExpressionDialogProps) {

    const icon = useIcon()
    const elementLoaded = useElementLoaded("expression-editor-dialog");
    const translate = useTranslation();
    const stringOperators = ['in', 'notIn', '=', '!=', '>', '<', '>=', '<=', 'contains', 'beginsWith', 'endsWith',
        'doesNotContain', 'doesNotBeginWith', 'doesNotEndWith', 'null', 'notNull']
    const integerOperators = ['in', 'notIn', '=', '!=', '>', '<', '>=', '<=']
    const booleanOperators = ['=', '!=']
    const _searchRef = createRef<FilterEditorRef>();
    const [_open, setOpen] = useState<boolean>(props.open);
    const [_query, setQuery] = useState<RuleGroupType>(initQuery())
    const [_fields, setFields] = useState<Field[]>([]);
    const [_focused, setFocused] = useState<boolean>(false);
    const ref = createRef<Dialog>()
    const [_uxManagement, clearUx, setUx] = useUxManagement<FilterUxData>('filterEditor')





    useEffect(() => {
        setOpen(props.open)
        setFocused(props.open)
    }, [props.open])

    useEffect(() => {
        if (props.fields) {
            let fields = new Array<Field>();
            for (let i = 0; i < props.fields.length; i++) {
                const item = props.fields[i];
                let field: Field = {
                    id: item.id,
                    name: item.name,
                    label: item.label ?? item.name,
                    inputType: item.dataType === "integer" || item.dataType === "float" ? "number" : "text"
                }
                fields.push(field)
                switch (item.dataType) {
                    case "integer":
                    case "float": field.operators = defaultOperators.filter(x => integerOperators.includes(x.name)); break;
                    case "boolean": field.operators = defaultOperators.filter(x => booleanOperators.includes(x.name)); break;
                    default: field.operators = defaultOperators.filter(x => stringOperators.includes(x.name)); break;
                }
            }
            setFields(fields)
        } else {
            setFields([])
        }
    }, [props.fields])


    /**
     * Cancels the dialog, resets visibility, and notifies the parent.
     */
    const handleClose = () => {
        setOpen(false)
        props.onCancel();
    }

    /**
     * Saves the current filter and closes the dialog.
     */
    const handleApplyClose = useCallback(() => {
        setOpen(false)
        let filter = queryToExpression(_query, _fields, props.scope)
        props.onSave(filter, true);
    }, [_query, props.scope, _fields])

    /**
     * Saves the current filter but keeps the dialog open.
     */
    const handleApply = useCallback(() => {
        let filter = queryToExpression(_query, _fields, props.scope)
        props.onSave(filter, false);
    }, [_query, props.scope, _fields])

    useEffect(() => {
        if (props.filter) {
            let query = expressionToQuery(props.filter)
            setQuery(query)
        } else {
            setQuery(initQuery())
        }
    }, [props.filter])

    /**
     * Updates local state when the query builder emits changes.
     * @param query Updated rule group from the filter editor.
     */
    const handleQueryChanged = (query?: RuleGroupType) => {
        if (query) {
            setQuery(query)
        }
    }

    /**
     * Renders the dialog action buttons for apply/cancel functionality.
     * @returns JSX fragment containing footer buttons.
     */
    const renderFooter = () => {
        return (
            <div>
                <Button label={translate("APPLY")} icon={icon(FabrixIcon.dialogSave)} onClick={handleApply} className="p-button-outlined fbx-button-outlined hover:surface-500" />
                <Button label={translate("APPLY_CLOSE")} icon={icon(FabrixIcon.dialogSave)} onClick={handleApplyClose} className="p-button-outlined fbx-button-outlined hover:surface-500" />
                <Button label={translate("CANCEL")} icon={icon(FabrixIcon.dialogCancel)} onClick={handleClose} className="p-button-outlined fbx-button-outlined p-button-secondary hover:surface-500" />
            </div>
        )
    }

    useEffect(() => {

        let dialog = elementLoaded;
        if (dialog) {
            dialog.onmouseenter = () => {
                setFocused(true)
            }
            dialog.onmouseleave = () => {
                setFocused(false)
            }
            dialog.onfocus = () => {
                setFocused(true)
            }
        }
    }, [elementLoaded])

    /**
     * Persists dialog dimensions when users finish resizing.
     * @param e Resize event emitted by PrimeReact dialog.
     */
    const handleResizeEnd = (e: any) => {
        let element = e.target?.offsetParent as HTMLDivElement
        if (element)
            setUx({  width: element.clientWidth, height: element.clientHeight } )
    }

    /**
     * Persists dialog position when users finish dragging.
     * @param e Drag event emitted by PrimeReact dialog.
     */
    const handleDragEnd = (e: any) => {
        let element = e.target?.offsetParent;
        if (element)
            setUx({ position: { x: element.offsetLeft, y: element.offsetTop } })
    }

    /**
     * Calculates persisted dialog positioning and sizing styles.
     * @returns Style object applied to the dialog.
     */
    const calcStyle = useCallback(() => {
        let style: CSSProperties = {
            position: 'fixed',
            top: _uxManagement?.position?.y,
            left: _uxManagement?.position?.x,
            margin: 0,
            width: _uxManagement?.width,
            height: _uxManagement?.height
        }
        return style;
    }, [_uxManagement])



    return (
        <Dialog id="expression-editor-dialog" ref={ref} closable={true} modal={false} onDragEnd={handleDragEnd} style={calcStyle()} onResizeEnd={handleResizeEnd}
            header={props.title ?? translate("FILTER")} className={`fbx-filter-dialog ${_focused ? 'fbx-filter-dialog-focused' : ''}`} visible={_open} footer={renderFooter()} onHide={() => handleClose()}>

            <FilterEditor ref={_searchRef} query={_query} fields={_fields} onChange={handleQueryChanged} />

        </Dialog>
    )
}